import api from '../../../services/api';
import {
  asArray,
  asBoolean,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from '../../../dto/common';
import { OrderDto, normalizeOrder, normalizeOrders } from '../dto';
import { OrderMarginHistoryEntry } from '../types';
import {
  getOrderBaseCurrency,
  normalizeCreateOrderProducts,
  OrderProductLineDraft,
} from '../orderProductLineTotals';

export type OrderMarginRange = 'low' | 'normal' | 'high';

export interface OrderMarginExplanationAllocationDto {
  linkId?: string | null;
  supplierOrderId: string;
  supplierOrderNumber?: string | null;
  supplierName?: string | null;
  supplierOrderItemId?: string | null;
  supplierOrderItemName?: string | null;
  allocatedQuantity?: number | null;
  allocatedUnit?: string | null;
  manualAllocatedCostPLN: number;
  effectiveAllocatedPurchaseCostPLN: number;
  effectiveAllocatedLandedCostPLN: number;
  effectiveAllocatedCostPLN: number;
  manualHistoricalCostPLN: number;
  roundingAdjustmentPLN: number;
}

export interface OrderMarginExplanationLineDto {
  id: string;
  productVariantId?: string | null;
  productCode?: string | null;
  catalogProductName?: string | null;
  name: string;
  quantity: number;
  unit: string;
  saleUnit: string;
  saleQuantity: number;
  unitPrice: number;
  currency: string;
  revenuePLN: number;
  supplierPurchaseCostPLN: number;
  supplierLandedCostPLN: number;
  supplierManualHistoricalCostPLN: number;
  supplierAllocationRoundingAdjustmentPLN: number;
  allocatedCostPLN: number;
  marginPLN: number;
  marginPercent: number;
  allocations: OrderMarginExplanationAllocationDto[];
}

export interface OrderMarginExplanationDto {
  order: {
    id: string;
    orderNumber: string;
    manualOrderNumber?: string | null;
    useManualNumbering: boolean;
    currency: string;
    exchangeRate?: number | null;
    exchangeRateSource?: string | null;
    exchangeRateTableNumber?: string | null;
    exchangeRateRequestedDate?: string | null;
    exchangeRateEffectiveDate?: string | null;
    customerName?: string | null;
  };
  totals: {
    revenuePLN: number;
    supplierAllocatedCostPLN: number;
    supplierPurchaseCostPLN: number;
    supplierLandedCostPLN: number;
    supplierManualHistoricalCostPLN: number;
    supplierAllocationRoundingAdjustmentPLN: number;
    cogsPLN: number;
    manualCostPLN: number;
    manualCustomerCostPLN: number;
    transportCostPLN: number;
    productFallbackCostPLN: number;
    unexplainedCostPLN: number;
    totalCostPLN: number;
    marginPLN: number;
    marginPercent: number;
    lineRevenuePLN: number;
    lineAllocatedCostPLN: number;
    unassignedAllocationCostPLN: number;
    reconciles: boolean;
  };
  lines: OrderMarginExplanationLineDto[];
  unassignedAllocations: OrderMarginExplanationAllocationDto[];
  orderLevelCosts: {
    cogsPLN: number;
    manualCostPLN: number;
    manualCustomerCostPLN: number;
    transportCostPLN: number;
    productFallbackCostPLN: number;
    unexplainedCostPLN: number;
  };
}

function normalizeOrderMarginExplanationAllocation(
  value: unknown,
  entity: string,
  index: number,
): OrderMarginExplanationAllocationDto {
  const record = asRecord(value);
  if (!record) {
    reportDtoAnomaly(entity, `allocations[${index}]`, value);
  }

  return {
    linkId: asOptionalString(record?.linkId) ?? null,
    supplierOrderId: asString(record?.supplierOrderId),
    supplierOrderNumber: asOptionalString(record?.supplierOrderNumber) ?? null,
    supplierName: asOptionalString(record?.supplierName) ?? null,
    supplierOrderItemId: asOptionalString(record?.supplierOrderItemId) ?? null,
    supplierOrderItemName: asOptionalString(record?.supplierOrderItemName) ?? null,
    allocatedQuantity: record?.allocatedQuantity == null ? null : asNumber(record.allocatedQuantity),
    allocatedUnit: asOptionalString(record?.allocatedUnit) ?? null,
    manualAllocatedCostPLN: asNumber(record?.manualAllocatedCostPLN),
    effectiveAllocatedPurchaseCostPLN: asNumber(record?.effectiveAllocatedPurchaseCostPLN),
    effectiveAllocatedLandedCostPLN: asNumber(record?.effectiveAllocatedLandedCostPLN),
    effectiveAllocatedCostPLN: asNumber(record?.effectiveAllocatedCostPLN),
    manualHistoricalCostPLN: asNumber(record?.manualHistoricalCostPLN),
    roundingAdjustmentPLN: asNumber(record?.roundingAdjustmentPLN),
  };
}

function normalizeOrderMarginExplanationLine(
  value: unknown,
  entity: string,
  index: number,
): OrderMarginExplanationLineDto {
  const record = asRecord(value);
  if (!record) {
    reportDtoAnomaly(entity, `lines[${index}]`, value);
  }

  return {
    id: asString(record?.id, `line-${index}`),
    productVariantId: asOptionalString(record?.productVariantId) ?? null,
    productCode: asOptionalString(record?.productCode) ?? null,
    catalogProductName: asOptionalString(record?.catalogProductName) ?? null,
    name: asString(record?.name),
    quantity: asNumber(record?.quantity),
    unit: asString(record?.unit, 'pcs'),
    saleUnit: asString(record?.saleUnit, asString(record?.unit, 'pcs')),
    saleQuantity: asNumber(record?.saleQuantity ?? record?.quantity),
    unitPrice: asNumber(record?.unitPrice),
    currency: asString(record?.currency, 'PLN'),
    revenuePLN: asNumber(record?.revenuePLN),
    supplierPurchaseCostPLN: asNumber(record?.supplierPurchaseCostPLN),
    supplierLandedCostPLN: asNumber(record?.supplierLandedCostPLN),
    supplierManualHistoricalCostPLN: asNumber(record?.supplierManualHistoricalCostPLN),
    supplierAllocationRoundingAdjustmentPLN: asNumber(record?.supplierAllocationRoundingAdjustmentPLN),
    allocatedCostPLN: asNumber(record?.allocatedCostPLN),
    marginPLN: asNumber(record?.marginPLN),
    marginPercent: asNumber(record?.marginPercent),
    allocations: asArray(record?.allocations, (item, allocationIndex) => (
      normalizeOrderMarginExplanationAllocation(item, `${entity}.lines[${index}]`, allocationIndex)
    )),
  };
}

export function normalizeOrderMarginExplanation(
  value: unknown,
  entity = 'orders.marginExplanation',
): OrderMarginExplanationDto {
  const record = asRecord(value);
  if (!record) {
    reportDtoAnomaly(entity, 'root', value);
  }

  const order = asRecord(record?.order);
  const totals = asRecord(record?.totals);
  const orderLevelCosts = asRecord(record?.orderLevelCosts);

  return {
    order: {
      id: asString(order?.id),
      orderNumber: asString(order?.orderNumber),
      manualOrderNumber: asOptionalString(order?.manualOrderNumber) ?? null,
      useManualNumbering: asBoolean(order?.useManualNumbering),
      currency: asString(order?.currency, 'PLN'),
      exchangeRate: order?.exchangeRate == null ? null : asNumber(order.exchangeRate),
      exchangeRateSource: asOptionalString(order?.exchangeRateSource) ?? null,
      exchangeRateTableNumber: asOptionalString(order?.exchangeRateTableNumber) ?? null,
      exchangeRateRequestedDate: asOptionalString(order?.exchangeRateRequestedDate) ?? null,
      exchangeRateEffectiveDate: asOptionalString(order?.exchangeRateEffectiveDate) ?? null,
      customerName: asOptionalString(order?.customerName) ?? null,
    },
    totals: {
      revenuePLN: asNumber(totals?.revenuePLN),
      supplierAllocatedCostPLN: asNumber(totals?.supplierAllocatedCostPLN),
      supplierPurchaseCostPLN: asNumber(totals?.supplierPurchaseCostPLN),
      supplierLandedCostPLN: asNumber(totals?.supplierLandedCostPLN),
      supplierManualHistoricalCostPLN: asNumber(totals?.supplierManualHistoricalCostPLN),
      supplierAllocationRoundingAdjustmentPLN: asNumber(totals?.supplierAllocationRoundingAdjustmentPLN),
      cogsPLN: asNumber(totals?.cogsPLN),
      manualCostPLN: asNumber(totals?.manualCostPLN),
      manualCustomerCostPLN: asNumber(totals?.manualCustomerCostPLN),
      transportCostPLN: asNumber(totals?.transportCostPLN),
      productFallbackCostPLN: asNumber(totals?.productFallbackCostPLN),
      unexplainedCostPLN: asNumber(totals?.unexplainedCostPLN),
      totalCostPLN: asNumber(totals?.totalCostPLN),
      marginPLN: asNumber(totals?.marginPLN),
      marginPercent: asNumber(totals?.marginPercent),
      lineRevenuePLN: asNumber(totals?.lineRevenuePLN),
      lineAllocatedCostPLN: asNumber(totals?.lineAllocatedCostPLN),
      unassignedAllocationCostPLN: asNumber(totals?.unassignedAllocationCostPLN),
      reconciles: asBoolean(totals?.reconciles),
    },
    lines: asArray(record?.lines, (item, index) => normalizeOrderMarginExplanationLine(item, entity, index)),
    unassignedAllocations: asArray(record?.unassignedAllocations, (item, index) => (
      normalizeOrderMarginExplanationAllocation(item, `${entity}.unassignedAllocations`, index)
    )),
    orderLevelCosts: {
      cogsPLN: asNumber(orderLevelCosts?.cogsPLN),
      manualCostPLN: asNumber(orderLevelCosts?.manualCostPLN),
      manualCustomerCostPLN: asNumber(orderLevelCosts?.manualCustomerCostPLN),
      transportCostPLN: asNumber(orderLevelCosts?.transportCostPLN),
      productFallbackCostPLN: asNumber(orderLevelCosts?.productFallbackCostPLN),
      unexplainedCostPLN: asNumber(orderLevelCosts?.unexplainedCostPLN),
    },
  };
}

export interface OrdersListQuery {
  status?: string[];
  customerId?: string;
  accountManagerId?: string;
  startDate?: string;
  endDate?: string;
  marginRange?: OrderMarginRange;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isQuote?: boolean;
}

export interface OrderSettingsDto {
  currency?: string;
}

export type OrderDocumentLanguage = 'pl' | 'en';
export type OrderDocumentConversionUnit = 'm2' | 'm3';

export async function fetchOrders(query: OrdersListQuery = {}, entity = 'orders.list'): Promise<OrderDto[]> {
  const params = new URLSearchParams();

  if (query.status && query.status.length > 0) {
    params.append('status', query.status.join(','));
  }

  if (query.customerId) {
    params.append('customerId', query.customerId);
  }

  if (query.accountManagerId) {
    params.append('accountManagerId', query.accountManagerId);
  }

  params.append('isQuote', String(Boolean(query.isQuote)));

  if (query.startDate) {
    params.append('startDate', query.startDate);
  }

  if (query.endDate) {
    params.append('endDate', query.endDate);
  }

  if (query.marginRange === 'low') {
    params.append('marginMax', '15');
  }

  if (query.marginRange === 'normal') {
    params.append('marginMin', '15');
    params.append('marginMax', '30');
  }

  if (query.marginRange === 'high') {
    params.append('marginMin', '30');
  }

  if (query.search) {
    params.append('search', query.search);
  }

  params.append('sortBy', query.sortBy || 'orderDate');
  params.append('sortOrder', query.sortOrder || 'desc');

  const response = await api.get(`/orders${params.toString() ? `?${params.toString()}` : ''}`);
  return normalizeOrders(response.data, entity);
}

export async function fetchOrderDetail(orderId: string, entity = 'orders.detail'): Promise<OrderDto> {
  const response = await api.get(`/orders/${orderId}`);
  return normalizeOrder(response.data, entity);
}

export async function fetchOrderSettings(): Promise<OrderSettingsDto> {
  const response = await api.get('/settings');
  return response.data;
}

export async function createOrder(payload: Record<string, unknown>): Promise<void> {
  await api.post('/orders', payload);
}

export interface CreateOrderWizardValues extends Record<string, unknown> {
  currency?: string;
  orderDate?: { toISOString: () => string };
  expectedDelivery?: { toISOString: () => string };
  customerPaymentDueDate?: { toISOString: () => string };
  customerPaymentNotes?: string | null;
  quoteValidUntil?: { toISOString: () => string };
  products?: OrderProductLineDraft[];
}

interface CreateOrderPayloadOptions {
  includeProductSalePrices?: boolean;
}

export function buildCreateOrderPayload(
  values: CreateOrderWizardValues,
  preferredCurrency = 'PLN',
  options: CreateOrderPayloadOptions = {},
): Record<string, unknown> {
  const products = values.products || [];
  const includeProductSalePrices = options.includeProductSalePrices !== false;
  const baseCurrency = getOrderBaseCurrency(
    products,
    values.currency || preferredCurrency || 'PLN'
  );
  const productCurrencies = Array.from(
    new Set(products.map((product) => product.currency).filter(Boolean))
  );
  const orderCurrency = productCurrencies.length > 1
    ? values.currency || preferredCurrency || 'PLN'
    : baseCurrency;

  return {
    ...values,
    currency: orderCurrency,
    orderDate: values.orderDate?.toISOString(),
    expectedDelivery: values.expectedDelivery?.toISOString(),
    customerPaymentDueDate: values.customerPaymentDueDate?.toISOString(),
    customerPaymentNotes: values.customerPaymentNotes || null,
    quoteValidUntil: values.quoteValidUntil?.toISOString(),
    products: normalizeCreateOrderProducts(products, baseCurrency, includeProductSalePrices),
  };
}

export async function createOrderFromWizard(
  values: CreateOrderWizardValues,
  preferredCurrency = 'PLN',
  options: CreateOrderPayloadOptions = {},
): Promise<OrderDto> {
  const response = await api.post('/orders', buildCreateOrderPayload(values, preferredCurrency, options));
  return normalizeOrder(response.data, 'orders.create');
}

export async function deleteOrder(orderId: string): Promise<void> {
  await api.delete(`/orders/${orderId}`);
}

export async function updateOrder(orderId: string, payload: Record<string, unknown>): Promise<void> {
  await api.put(`/orders/${orderId}`, payload);
}

export async function fetchSuggestedManualOrderNumber(orderDate?: string): Promise<string> {
  const response = await api.get('/orders/suggest-number', {
    params: orderDate ? { orderDate } : undefined,
  });
  return response.data.suggestedNumber;
}

export async function convertQuoteToOrder(orderId: string): Promise<unknown> {
  const response = await api.post(`/orders/${orderId}/convert-quote`);
  return response.data;
}

export async function settleOrder(orderId: string): Promise<unknown> {
  const response = await api.post(`/orders/${orderId}/settle`);
  return response.data;
}

export async function fetchOrderMarginHistory(orderId: string, entity = 'orders.marginHistory'): Promise<OrderMarginHistoryEntry[]> {
  const response = await api.get(`/orders/${orderId}/margin-history`);
  return normalizeOrderMarginHistory(response.data, entity);
}

export async function fetchOrderMarginExplanation(orderId: string): Promise<OrderMarginExplanationDto> {
  const response = await api.get(`/orders/${orderId}/margin-explanation`);
  return normalizeOrderMarginExplanation(response.data);
}

export async function fetchOrderProductSuggestions(): Promise<unknown[]> {
  const response = await api.get('/orders/product-names');
  return response.data;
}

export async function generateWzFromOrder(orderId: string): Promise<unknown> {
  const response = await api.post(`/wz/generate/${orderId}`);
  return response.data;
}

export async function downloadOrderPdf(
  orderId: string,
  language: OrderDocumentLanguage,
  conversionUnit?: OrderDocumentConversionUnit,
): Promise<Blob> {
  const params = new URLSearchParams({ lang: language });
  if (conversionUnit) {
    params.set('conversionUnit', conversionUnit);
  }

  const response = await api.get(`/orders/${orderId}/pdf?${params.toString()}`, {
    responseType: 'blob',
  });

  return response.data;
}

function normalizeOrderMarginHistory(raw: unknown, entity: string): OrderMarginHistoryEntry[] {
  return asArray(raw, (item, index) => normalizeOrderMarginHistoryEntry(item, `${entity}[${index}]`));
}

function normalizeOrderMarginHistoryEntry(raw: unknown, entity: string): OrderMarginHistoryEntry {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const changedByRecord = asRecord(record?.changedBy);

  if (record?.changedBy != null && !changedByRecord) {
    reportDtoAnomaly(entity, 'changedBy', record?.changedBy);
  }

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    revenue: asNumber(record?.revenue),
    totalCost: asNumber(record?.totalCost),
    margin: asNumber(record?.margin),
    marginPercent: asNumber(record?.marginPercent),
    reason: asString(record?.reason),
    changedAt: asString(record?.changedAt),
    changedBy: changedByRecord
      ? {
          id: asString(changedByRecord.id),
          name: asString(changedByRecord.name),
          email: asOptionalString(changedByRecord.email),
        }
      : null,
  };
}
